import React from "react"
import { IoIosArrowBack } from "react-icons/io"
import { IoClose, IoTrashOutline } from "react-icons/io5"
import Modal from "react-modal"
import { BottomSheet } from "react-spring-bottom-sheet"
import "react-spring-bottom-sheet/dist/style.css"

import { InfoModalComponent } from "@fuse/sharedComponents/plugin/InfoModalComponent"
import InfoStepsCard from "@fuse/sharedComponents/plugin/InfoStepsCard"
import { getPluginEventInstanceData } from "@fuse/api/pluginDataApi"
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react"
import moment from "moment"

// CSS fallback
import { cn } from "@fuse/utils/cn"
import { deleteImageFromStorage, uploadImageToPath } from "@fuse/utils/Firebase"

import ImgCameraAdd from "@fuse/assets/images/plugin/img-camera-add.svg"
import ImgPhotoGallery from "@fuse/assets/images/plugin/img-photo-gallery.svg"

const pluginImages = {
  imgCameraAdd: ImgCameraAdd,
  imgPhotoGallery: ImgPhotoGallery,
}

/**
 * Plugin UI Component Library
 *
 * IMPORTANT: All components used in the plugin templates (defaultPluginTemplates.ts)
 * must be defined here. If a component is referenced in a template but not defined here,
 * plugins will show errors like "expected a string but got: undefined".
 *
 * The plugin sandbox provides fallbacks for missing components, but it's better
 * to explicitly define all required components here.
 *
 * SYNC REQUIREMENT: This pluginComponents object structure must be kept in sync with the Monaco editor type definitions
 * in apps/website/src/@fuse/pages/plugins/types/pluginEditorTypes.ts (PluginAPI.Components namespace)
 *
 * When adding/modifying components:
 * 1. Update the corresponding component interface in pluginEditorTypes.ts
 * 2. Ensure prop types and component names match exactly
 * 3. Document any new components in the Monaco editor type definitions
 */

// Define common plugin UI components
export const pluginComponents = {
  Button: ({
    children,
    onClick,
    variant = "primary",
    size = "medium",
    disabled = false,
    fullWidth = false,
    className = "",
    ...props
  }) => {
    // Base classes
    const baseClasses =
      "font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-50"

    // Size classes
    const sizeClasses = {
      small: "px-3 py-1 text-sm",
      medium: "px-4 py-2",
      large: "px-5 py-3 text-lg",
    }

    // Variant classes
    const variantClasses = {
      primary: "bg-blue-500 hover:bg-blue-600 text-white focus:ring-blue-300",
      secondary:
        "bg-gray-200 hover:bg-gray-300 text-gray-800 focus:ring-gray-200",
      success:
        "bg-green-500 hover:bg-green-600 text-white focus:ring-green-300",
      danger: "bg-red-500 hover:bg-red-600 text-white focus:ring-red-300",
      outline:
        "bg-transparent border border-gray-300 hover:bg-gray-50 text-gray-700 focus:ring-gray-200",
      link: "bg-transparent text-blue-500 hover:text-blue-700 hover:underline p-0",
    }

    // Disabled state
    const disabledClasses = disabled
      ? "opacity-50 cursor-not-allowed"
      : "cursor-pointer"

    // Full width
    const widthClasses = fullWidth ? "w-full" : ""

    const classes = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${disabledClasses} ${widthClasses} ${className}`

    return (
      <button
        className={classes}
        onClick={onClick}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    )
  },

  // App-styled component that matches the "Create your own event" button
  AppButton: ({
    children,
    onClick,
    disabled = false,
    fullWidth = false,
    className = "",
    ...props
  }) => {
    return (
      <button
        className={`w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium transition-colors ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        } ${className}`}
        onClick={onClick}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    )
  },

  // Enhanced glasomorphism button for zero-config plugins
  PrimaryButton: ({
    children,
    onClick,
    variant = "primary",
    size = "medium",
    disabled = false,
    loading = false,
    icon,
    iconPosition = "left",
    className = "",
    ...props
  }) => {
    const sizeClasses = {
      small: "px-3 py-2 text-sm",
      medium: "px-4 py-3 text-base",
      large: "px-6 py-4 text-lg",
    }

    const variantClasses = {
      primary:
        "bg-blue-500/80 hover:bg-blue-500/90 text-white border-blue-400/30",
      secondary: "bg-white/10 hover:bg-white/20 text-white border-white/20",
      ghost: "bg-transparent hover:bg-white/10 text-white border-transparent",
    }

    return (
      <button
        className={cn(
          // Base button styling
          "relative rounded-xl backdrop-blur-sm border transition-all duration-200",
          "font-medium focus:outline-none focus:ring-2 focus:ring-white/30",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          // Size and variant
          sizeClasses[size],
          variantClasses[variant],
          // Loading state
          loading && "cursor-wait",
          className,
        )}
        onClick={onClick}
        disabled={disabled || loading}
        {...props}
      >
        <span
          className={cn(
            "flex items-center justify-center gap-2",
            loading && "opacity-0",
          )}
        >
          {icon && iconPosition === "left" && icon}
          {children}
          {icon && iconPosition === "right" && icon}
        </span>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full border-2 border-white/20 border-t-white w-4 h-4" />
          </div>
        )}
      </button>
    )
  },

  // App-styled header that matches the "About this event" styling
  EventHeader: ({ title, children, className = "", ...props }) => {
    return (
      <div
        className={`bg-neutral-800/80 rounded-2xl p-5 mb-4 text-white ${className}`}
        {...props}
      >
        {title && <h2 className="text-2xl font-medium mb-3">{title}</h2>}
        <div className="text-white/90 text-lg">{children}</div>
      </div>
    )
  },

  // App-styled content panel for general content
  ContentPanel: ({ children, title, className = "", ...props }) => {
    return (
      <div
        className={`bg-neutral-800/80 rounded-2xl border border-white/10 p-5 mb-4 ${className}`}
        {...props}
      >
        {title && (
          <h3 className="text-xl font-medium text-white mb-3">{title}</h3>
        )}
        <div>{children}</div>
      </div>
    )
  },

  // Enhanced glasomorphism container for zero-config plugins
  PluginContainer: ({ className = "", children, ...props }) => {
    // Minimal container since PluginRenderer already provides the glasomorphism wrapper
    return (
      <div
        className={cn(
          // Just basic spacing and layout
          "w-full",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    )
  },

  // Enhanced glasomorphism card
  BaseCard: ({
    className = "",
    children,
    interactive = false,
    padding = "md",
    ...props
  }) => {
    const paddingClasses = {
      sm: "p-3",
      md: "p-4",
      lg: "p-6",
    }

    return (
      <div
        className={cn(
          // Base card styling
          "rounded-xl bg-white/5 border border-white/10",
          paddingClasses[padding],
          // Interactive states
          interactive &&
            "cursor-pointer transition-all duration-200 hover:bg-white/10 hover:border-white/20",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    )
  },

  // Enhanced TextArea component that matches the event creation view's styling
  TextArea: ({
    label,
    value,
    onChange,
    placeholder = "Describe your event",
    rows = 5,
    error = "",
    disabled = false,
    className = "",
    showCharCount = false,
    maxLength,
    ...props
  }) => {
    const id = React.useId()
    const [focused, setFocused] = React.useState(false)
    const charCount = typeof value === "string" ? value.length : 0

    return (
      <div className={`mb-4 ${className}`}>
        {label && (
          <label htmlFor={id} className="block mb-1 font-medium text-gray-100">
            {label}
          </label>
        )}
        <div
          className={`relative bg-neutral-700/40 backdrop-blur-md rounded-3xl transition-all ${
            focused ? "ring-2 ring-white/30" : ""
          }`}
        >
          <textarea
            id={id}
            value={value}
            onChange={(e) =>
              onChange(typeof onChange === "function" ? e.target.value : e)
            }
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            maxLength={maxLength}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className={`w-full h-full bg-transparent rounded-3xl border-0 text-white placeholder-gray-400 p-6 resize-none focus:ring-0 focus:outline-none ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            {...props}
          />
          {((value && value.length > 0) || (showCharCount && maxLength)) && (
            <div className="absolute bottom-2 right-4 text-xs text-white/50">
              {showCharCount && maxLength
                ? `${charCount}/${maxLength}`
                : `${charCount} characters`}
            </div>
          )}
        </div>
        {error && (
          <div className="text-xs mt-1 flex items-center gap-1 text-red-400">
            <svg
              className="w-3 h-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}
      </div>
    )
  },

  // Simple emoji picker component that looks like Slack's
  EmojiPicker: ({ value = "🔗", onChange, className = "", ...props }) => {
    const [isOpen, setIsOpen] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState("")

    // Organized emoji categories like Slack
    const emojiCategories = {
      "Frequently Used": [
        "🔗",
        "📎",
        "🌐",
        "📱",
        "💻",
        "📧",
        "📞",
        "📍",
        "🎵",
        "🎶",
        "📸",
        "📹",
        "📄",
        "📋",
        "📊",
        "📈",
      ],
      "Objects & Symbols": [
        "🎉",
        "🎊",
        "🎁",
        "❤️",
        "⭐",
        "✨",
        "🔥",
        "👍",
        "📚",
        "🎓",
        "🏆",
        "🎯",
        "💡",
        "🚀",
        "⚡",
        "🌟",
      ],
      Activities: [
        "⚽",
        "🏀",
        "🎾",
        "🏈",
        "🎮",
        "🎲",
        "🎪",
        "🎭",
        "🎨",
        "🎬",
        "🎤",
        "🎧",
        "🎸",
        "🥳",
        "🎂",
        "🍕",
      ],
    }

    const allEmojis = Object.values(emojiCategories).flat()
    const filteredEmojis = searchTerm
      ? allEmojis.filter((emoji) =>
          // Simple search - could be enhanced with emoji names
          emoji.includes(searchTerm),
        )
      : null

    return (
      <div className={cn("relative", className)} {...props}>
        <div
          className="text-center bg-transparent text-white cursor-pointer"
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
        >
          <p className="text-sm text-center">{value}</p>
        </div>

        <Modal
          isOpen={isOpen}
          onRequestClose={() => setIsOpen(false)}
          className="fixed left-0 right-0 bottom-0 z-50 bg-neutral-900 rounded-t-2xl shadow-lg p-4 mx-auto outline-none border-0 rsbs-theme-dark"
          overlayClassName="fixed inset-0 bg-black/40 flex items-end justify-center z-50"
          shouldCloseOnOverlayClick={true}
          ariaHideApp={false}
        >
          <div className="flex flex-col items-center w-full">
            <div className="w-12 h-1.5 bg-white/20 rounded-full mb-4 mx-auto" />
            <EmojiPicker
              theme={Theme.DARK}
              width={"100%"}
              onEmojiClick={(emojiData) => {
                onChange(emojiData.emoji)
                setIsOpen(false)
              }}
              lazyLoadEmojis
              searchDisabled={false}
              skinTonesDisabled={true}
              emojiStyle={EmojiStyle.NATIVE} // or "apple" / "google" / "twitter"
            />
          </div>
        </Modal>
      </div>
    )
  },

  // Enhanced glasomorphism text input with no unnecessary borders
  TextInput: ({
    label,
    value,
    onChange,
    placeholder,
    type = "text",
    error = "",
    disabled = false,
    className = "",
    icon,
    onFocus,
    onBlur,
    ...props
  }) => {
    const id = React.useId()
    const [focused, setFocused] = React.useState(false)

    const handleFocus = (e) => {
      setFocused(true)
      onFocus?.(e)
    }

    const handleBlur = (e) => {
      setFocused(false)
      onBlur?.(e)
    }

    return (
      <div className={`space-y-1 ${className}`}>
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-white/90"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {icon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60">
              {icon}
            </div>
          )}
          <input
            id={id}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={cn(
              // Base styling - minimal, clean
              "w-full rounded-lg bg-transparent border-0 outline-0 text-sm",
              "text-white placeholder-white/50",
              "transition-all duration-200",
              // Subtle background only on focus
              // "focus:bg-white/5",
              // Icon padding
              icon && "pl-10",
              // Error states
              error && "text-red-300 placeholder-red-400/50",
            )}
            {...props}
          />
        </div>
        {error && (
          <div className="text-xs mt-1 flex items-center gap-1 text-red-400">
            <svg
              className="w-3 h-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}
      </div>
    )
  },

  // Consistent error text component
  ErrorText: ({ children, type = "error", className = "", ...props }) => {
    const typeStyles = {
      error: "text-red-400",
      warning: "text-yellow-400",
      info: "text-blue-400",
    }

    return (
      <div
        className={cn(
          "text-xs mt-1 flex items-center gap-1",
          typeStyles[type],
          className,
        )}
        {...props}
      >
        {type === "error" && (
          <svg
            className="w-3 h-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}
        {type === "warning" && (
          <svg
            className="w-3 h-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        )}
        <span>{children}</span>
      </div>
    )
  },

  Checkbox: ({
    label,
    checked,
    onChange,
    disabled = false,
    className = "",
    ...props
  }) => {
    const id = React.useId()

    return (
      <div className={`flex items-center mb-2 ${className}`}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className={`w-4 h-4 text-blue-600 border-gray-300 rounded 
            focus:ring-blue-500 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          {...props}
        />
        {label && (
          <label
            htmlFor={id}
            className={`ml-2 block text-gray-700 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          >
            {label}
          </label>
        )}
      </div>
    )
  },

  Card: ({ children, title, footer, className = "", ...props }) => {
    return (
      <div
        className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${className}`}
        {...props}
      >
        {title && (
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-medium text-gray-800">{title}</h3>
          </div>
        )}
        <div className="p-4">{children}</div>
        {footer && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            {footer}
          </div>
        )}
      </div>
    )
  },

  Avatar: ({
    src,
    alt = "User avatar",
    size = "medium",
    className = "",
    fallback,
    ...props
  }) => {
    const [imageError, setImageError] = React.useState(false)

    const sizeClasses = {
      small: "w-8 h-8 text-xs",
      medium: "w-10 h-10 text-sm",
      large: "w-12 h-12 text-base",
      xl: "w-16 h-16 text-lg",
    }

    return (
      <div
        className={cn(
          "relative rounded-full bg-white/10 backdrop-blur-sm border border-white/20",
          "flex items-center justify-center overflow-hidden",
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {src && !imageError ? (
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <span className="font-medium text-white/80">
            {fallback || alt.substring(0, 1).toUpperCase()}
          </span>
        )}
      </div>
    )
  },

  Badge: ({
    children,
    variant = "default",
    size = "md",
    className = "",
    ...props
  }) => {
    const sizeClasses = {
      sm: "px-2 py-1 text-xs",
      md: "px-3 py-1 text-sm",
    }

    const variantClasses = {
      default: "bg-white/10 text-white border-white/20",
      primary: "bg-blue-100 text-blue-800",
      success: "bg-green-500/20 text-green-300 border-green-400/30",
      warning: "bg-yellow-500/20 text-yellow-300 border-yellow-400/30",
      danger: "bg-red-500/20 text-red-300 border-red-400/30",
      error: "bg-red-500/20 text-red-300 border-red-400/30",
      info: "bg-blue-500/20 text-blue-300 border-blue-400/30",
    }

    return (
      <span
        className={cn(
          "inline-flex items-center rounded-full border backdrop-blur-sm font-medium",
          sizeClasses[size],
          variantClasses[variant],
          className,
        )}
        {...props}
      >
        {children}
      </span>
    )
  },

  Alert: ({ children, type = "info", onClose, className = "", ...props }) => {
    const typeClasses = {
      info: "bg-blue-50 text-blue-700 border-blue-100",
      success: "bg-green-50 text-green-700 border-green-100",
      warning: "bg-yellow-50 text-yellow-700 border-yellow-100",
      error: "bg-red-50 text-red-700 border-red-100",
    }

    return (
      <div
        className={`p-4 mb-4 border-l-4 ${typeClasses[type]} ${className}`}
        role="alert"
        {...props}
      >
        <div className="flex items-start">
          <div className="flex-grow">{children}</div>
          {onClose && (
            <button
              onClick={onClose}
              className="ml-auto -mx-1.5 -my-1.5 text-gray-400 hover:text-gray-900 rounded-lg p-1.5"
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>
      </div>
    )
  },

  Spinner: ({ size = "medium", className = "", ...props }) => {
    const sizeClasses = {
      small: "w-4 h-4",
      medium: "w-6 h-6",
      large: "w-8 h-8",
    }

    return (
      <svg
        className={`animate-spin text-blue-500 ${sizeClasses[size]} ${className}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        {...props}
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    )
  },

  // Enhanced glasomorphism loading spinner
  LoadingSpinner: ({ className = "", size = "md", ...props }) => {
    const sizeClasses = {
      sm: "w-4 h-4",
      md: "w-6 h-6",
      lg: "w-8 h-8",
    }

    return (
      <div
        className={cn(
          "animate-spin rounded-full border-2 border-white/20 border-t-white",
          sizeClasses[size],
          className,
        )}
        {...props}
      />
    )
  },

  Tabs: ({ tabs, activeTab, onChange, className = "", ...props }) => {
    return (
      <div className={`mb-4 ${className}`} {...props}>
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onChange(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                aria-current={activeTab === tab.id ? "page" : undefined}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>
    )
  },

  // Enhanced glasomorphism components for zero-config plugins
  Heading: ({
    className = "",
    children,
    level = 2,
    gradient = false,
    ...props
  }) => {
    const Component = `h${level}`

    const sizeClasses = {
      1: "text-4xl sm:text-5xl font-bold",
      2: "text-3xl sm:text-4xl font-bold",
      3: "text-2xl sm:text-3xl font-semibold",
      4: "text-xl sm:text-2xl font-semibold",
      5: "text-lg sm:text-xl font-medium",
      6: "text-base sm:text-lg font-medium",
    }

    return React.createElement(
      Component,
      {
        className: cn(
          sizeClasses[level],
          gradient
            ? "bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent"
            : "text-white",
          className,
        ),
        ...props,
      },
      children,
    )
  },

  ProgressBar: ({
    className = "",
    value,
    max = 100,
    showLabel = false,
    color = "blue",
    ...props
  }) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100)

    const colorClasses = {
      blue: "bg-blue-500",
      green: "bg-green-500",
      yellow: "bg-yellow-500",
      red: "bg-red-500",
    }

    return (
      <div className={cn("space-y-2", className)} {...props}>
        {showLabel && (
          <div className="flex justify-between text-sm text-white/80">
            <span>Progress</span>
            <span>{Math.round(percentage)}%</span>
          </div>
        )}
        <div className="w-full bg-white/10 rounded-full h-2 backdrop-blur-sm">
          <div
            className={cn(
              "h-2 rounded-full transition-all duration-300",
              colorClasses[color],
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    )
  },

  LikeButton: ({
    className = "",
    liked = false,
    count = 0,
    showCount = true,
    onClick,
    ...props
  }) => {
    const [isAnimating, setIsAnimating] = React.useState(false)

    const handleClick = (e) => {
      setIsAnimating(true)
      setTimeout(() => setIsAnimating(false), 300)
      onClick?.(e)
    }

    return (
      <button
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200",
          "bg-white/5 hover:bg-white/10 border border-white/10",
          "focus:outline-none focus:ring-2 focus:ring-white/30",
          className,
        )}
        onClick={handleClick}
        {...props}
      >
        <span
          className={cn(
            "transition-all duration-200",
            liked ? "text-red-400 scale-110" : "text-white/60",
            isAnimating && "animate-pulse scale-125",
          )}
        >
          {liked ? "❤️" : "🤍"}
        </span>
        {showCount && <span className="text-sm text-white/80">{count}</span>}
      </button>
    )
  },

  Countdown: ({ className = "", targetDate, onComplete, ...props }) => {
    const [timeLeft, setTimeLeft] = React.useState({
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    })

    React.useEffect(() => {
      const timer = setInterval(() => {
        const now = new Date().getTime()
        const distance = targetDate.getTime() - now

        if (distance < 0) {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
          onComplete?.()
          clearInterval(timer)
          return
        }

        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor(
            (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          ),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        })
      }, 1000)

      return () => clearInterval(timer)
    }, [targetDate, onComplete])

    const TimeUnit = ({ value, label }) => (
      <div className="flex flex-col items-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-4 min-w-[80px]">
          <span className="text-3xl font-bold text-white">
            {value.toString().padStart(2, "0")}
          </span>
        </div>
        <span className="text-sm text-white/60 mt-2">{label}</span>
      </div>
    )

    return (
      <div className={cn("flex justify-center gap-4", className)} {...props}>
        {timeLeft.days > 0 && <TimeUnit value={timeLeft.days} label="Days" />}
        <TimeUnit value={timeLeft.hours} label="Hours" />
        <TimeUnit value={timeLeft.minutes} label="Minutes" />
        <TimeUnit value={timeLeft.seconds} label="Seconds" />
      </div>
    )
  },

  FileDropzone: ({
    className = "",
    onFilesDrop,
    accept,
    multiple = false,
    maxSize,
    children,
    userId,
    eventId,
    pluginId,
    ...props
  }) => {
    const [isDragOver, setIsDragOver] = React.useState(false)
    const [uploading, setUploading] = React.useState(false)
    const fileInputRef = React.useRef(null)

    const handleDragOver = (e) => {
      e.preventDefault()
      setIsDragOver(true)
    }

    const handleDragLeave = (e) => {
      e.preventDefault()
      setIsDragOver(false)
    }

    const handleDrop = async (e) => {
      e.preventDefault()
      setIsDragOver(false)
      const files = e.dataTransfer.files

      if (files.length > 0) {
        if (uploadImageToPath && eventId && pluginId && userId) {
          setUploading(true)
          try {
            const urls = []
            for (let i = 0; i < files.length; i++) {
              const url = URL.createObjectURL(files[i])
              const downloadURL = await uploadImageToPath(
                url,
                `events/${eventId}/plugins/${pluginId}/${userId}/${Date.now()}_${files[i].name}`,
                userId,
              )
              urls.push(downloadURL)
            }
            onFilesDrop(urls)
          } finally {
            setUploading(false)
          }
        } else {
          onFilesDrop(files)
        }
      }
    }

    const handleFileSelect = async (e) => {
      const files = e.target.files
      if (files && files.length > 0) {
        if (uploadImageToPath && eventId && pluginId && userId) {
          setUploading(true)
          try {
            const urls = []
            for (let i = 0; i < files.length; i++) {
              const url = URL.createObjectURL(files[i])
              const downloadURL = await uploadImageToPath(
                url,
                `events/${eventId}/plugins/${pluginId}/${userId}/${Date.now()}_${files[i].name}`,
                userId,
              )
              urls.push(downloadURL)
            }
            onFilesDrop(urls)
          } finally {
            setUploading(false)
          }
        } else {
          onFilesDrop(files)
        }
      }
    }

    return (
      <div
        className={cn(
          "relative rounded-xl text-center",
          "cursor-pointer",
          className,
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        {...props}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
        />
        {children || (
          <div className="space-y-4">
            <div className="text-4xl">📁</div>
            <div>
              <p className="text-white/80 font-medium">
                Drop files here or click to browse
              </p>
              <p className="text-white/60 text-sm mt-1">
                {accept && `Accepts: ${accept}`}
                {maxSize &&
                  ` • Max size: ${(maxSize / 1024 / 1024).toFixed(1)}MB`}
              </p>
            </div>
          </div>
        )}
        {uploading && (
          <div className="text-blue-400 text-sm mt-2">Uploading photo...</div>
        )}
      </div>
    )
  },

  Timestamp: ({ className = "", date, relative = true, ...props }) => {
    const [displayTime, setDisplayTime] = React.useState("")

    React.useEffect(() => {
      const updateTime = () => {
        if (relative) {
          const now = new Date()
          const diff = now.getTime() - date.getTime()
          const seconds = Math.floor(diff / 1000)
          const minutes = Math.floor(seconds / 60)
          const hours = Math.floor(minutes / 60)
          const days = Math.floor(hours / 24)

          if (days > 0) {
            setDisplayTime(`${days} day${days > 1 ? "s" : ""} ago`)
          } else if (hours > 0) {
            setDisplayTime(`${hours} hour${hours > 1 ? "s" : ""} ago`)
          } else if (minutes > 0) {
            setDisplayTime(`${minutes} minute${minutes > 1 ? "s" : ""} ago`)
          } else {
            setDisplayTime("Just now")
          }
        } else {
          setDisplayTime(date.toLocaleDateString())
        }
      }

      updateTime()
      const interval = setInterval(updateTime, 60000) // Update every minute

      return () => clearInterval(interval)
    }, [date, relative])

    return (
      <time
        className={cn("text-sm text-white/60", className)}
        dateTime={date.toISOString()}
        title={date.toLocaleString()}
        {...props}
      >
        {displayTime}
      </time>
    )
  },

  ContentGrid: ({
    className = "",
    children,
    cols = 1,
    gap = "md",
    ...props
  }) => {
    const colClasses = {
      1: "grid-cols-1",
      2: "grid-cols-1 sm:grid-cols-2",
      3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
      4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
      6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
      12: "grid-cols-4 sm:grid-cols-6 lg:grid-cols-12",
    }

    const gapClasses = {
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
    }

    return (
      <div
        className={cn("grid", colClasses[cols], gapClasses[gap], className)}
        {...props}
      >
        {children}
      </div>
    )
  },

  // Secondary button with transparent styling
  SecondaryButton: ({
    children,
    onClick,
    disabled = false,
    loading = false,
    icon,
    iconPosition = "left",
    className = "",
    ...props
  }) => {
    return (
      <button
        className={cn(
          // Base transparent button styling
          "relative px-3 py-2 rounded-lg transition-all duration-200",
          "text-white/70 hover:text-white hover:bg-white/10",
          "focus:outline-none focus:ring-2 focus:ring-white/30",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          // Loading state
          loading && "cursor-wait",
          className,
        )}
        onClick={onClick}
        disabled={disabled || loading}
        {...props}
      >
        <span
          className={cn(
            "flex items-center justify-center gap-2",
            loading && "opacity-0",
          )}
        >
          {icon && iconPosition === "left" && icon}
          {children}
          {icon && iconPosition === "right" && icon}
        </span>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full border-2 border-white/20 border-t-white w-3 h-3" />
          </div>
        )}
      </button>
    )
  },

  // Icon component for rendering react-icons with customizable props
  Icon: ({
    icon: IconComponent,
    className = "",
    size = "md",
    color = "currentColor",
    ...props
  }) => {
    const sizeClasses = {
      xs: "w-3 h-3",
      sm: "w-4 h-4",
      md: "w-5 h-5",
      lg: "w-6 h-6",
      xl: "w-8 h-8",
      "2xl": "w-10 h-10",
    }

    if (!IconComponent) {
      return null
    }

    return (
      <IconComponent
        className={cn(
          sizeClasses[size],
          "text-white/80", // Default color that works well with the glasomorphism theme
          className,
        )}
        style={{ color }}
        {...props}
      />
    )
  },

  // Renders a single plugin image based on imageKey
  PluginImage: ({ imageKey, className = "", ...props }) => {
    const Img = pluginImages[imageKey]
    if (!Img) return null
    return <img src={Img} className={className} {...props} />
  },

  ImageModal: ({
    isOpen,
    onClose,
    imageSrc,
    timestamp,
    isHost,
    onDelete,
    className = "",
    ...props
  }) => {
    React.useEffect(() => {
      const handleEsc = (event) => {
        if (event.key === "Escape") {
          onClose()
        }
      }

      if (isOpen) {
        window.addEventListener("keydown", handleEsc)
      }

      return () => {
        window.removeEventListener("keydown", handleEsc)
      }
    }, [isOpen, onClose])

    if (!isOpen) return null

    const formatFullTimestamp = (date) => {
      if (!date) return ""

      const target = moment(date?.toDate?.() || date)
      if (!target.isValid()) return "Invalid date"

      const time = target.format("h:mm A")

      if (target.isSame(moment(), "day")) return `Today ${time}`
      if (target.isSame(moment().subtract(1, "day"), "day"))
        return `Yesterday ${time}`

      return `${target.format("L")} ${time}` // L = localized date
    }

    const handleContentClick = (e) => {
      e.stopPropagation()
    }

    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        overlayClassName="fixed inset-0 bg-black/20 flex items-center justify-center z-50"
        className={cn(
          "relative p-4 bg-black/30 backdrop-blur-md rounded-xl shadow-lg flex flex-col w-full max-w-sm outline-none border-0",
          className,
        )}
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        {...props}
      >
        <button
          onClick={onClose}
          className="text-white/70 text-2xl hover:text-white z-10 bg-transparent flex items-end justify-end p-0 pb-4"
          aria-label="Close"
        >
          <IoClose className="text-white/70" />
        </button>
        <div className="flex-grow flex items-center justify-center overflow-hidden">
          <img
            src={imageSrc}
            alt="Gallery view"
            className="w-full max-h-full object-contain rounded-lg"
            style={{ maxHeight: "calc(90vh - 120px)" }}
          />
        </div>
        <div className="mt-3 flex justify-between items-center text-white">
          <span className="text-sm text-white/70">
            {formatFullTimestamp(timestamp)}
          </span>
          {isHost && (
            <button
              onClick={async (e) => {
                e.stopPropagation()
                if (!onDelete) return
                deleteImageFromStorage(imageSrc)
                  .then(() => {
                    onDelete()
                  })
                  .catch((error) => {
                    console.error("Failed to delete image:", error)
                  })
              }}
              className="flex items-center justify-center gap-1.5 text-sm text-red-500 hover:text-red-400 py-0 bg-transparent"
            >
              <IoTrashOutline className="h-4 w-4" />
              Delete
            </button>
          )}
        </div>
      </Modal>
    )
  },

  PhotoGalleryModal: ({
    isOpen,
    onClose,
    photos = [],
    onPhotoClick,
    onFilesDrop,
    userId,
    eventId,
    pluginId,
    flyerImage,
    showImageModal,
    selectedImage,
    onCloseImageModal,
    onDeleteImage,
    pluginComponents,
    isHost,
    multiple = false,
  }) => {
    const { FileDropzone, PluginImage, BaseCard } = pluginComponents || {}
    const formatFullTimestamp = (date) => {
      if (!date) return ""

      const target = moment(date?.toDate?.() || date)
      if (!target.isValid()) return "Invalid date"

      const time = target.format("h:mm A")

      if (target.isSame(moment(), "day")) return `Today ${time}`
      if (target.isSame(moment().subtract(1, "day"), "day"))
        return `Yesterday ${time}`

      return `${target.format("L")} ${time}` // L = localized date
    }
    return (
      <>
        {/* Gallery Modal */}
        <Modal
          isOpen={isOpen}
          onRequestClose={onClose}
          className="fixed inset-0 flex flex-col outline-none"
          overlayClassName="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
          ariaHideApp={false}
        >
          <div
            className="absolute flex w-full h-full object-cover bg-cover bg-no-repeat"
            style={{
              background: "url(" + flyerImage + ")",
              backgroundPosition: "center",
            }}
          >
            <div className="fixed inset-0  flex flex-col outline-none bg-black bg-opacity-75 backdrop-blur-md">
              <div className="flex items-center justify-between px-6 py-8 bg-white/10">
                <div
                  className="text-white/30 text-base cursor-pointer p-2 bg-white/30 rounded-full"
                  onClick={onClose}
                >
                  <IoIosArrowBack color={"white"} size={20} />
                </div>
                <span className="text-white text-xl font-semibold">
                  Photo gallery
                </span>
                <div style={{ width: 40 }} />
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                  {photos.map((photo, idx) => (
                    <div
                      key={photo.id ?? photo.url ?? idx}
                      className="rounded-lg overflow-hidden cursor-pointer"
                      onClick={() => {
                        onPhotoClick(photo)
                      }}
                    >
                      <img
                        src={photo.url}
                        alt="Event photo"
                        className="w-full h-28 object-cover rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>
              {isHost && (
                <div className="px-6 w-full mb-6 fixed bottom-0">
                  <FileDropzone
                    accept="image/*"
                    multiple={multiple}
                    userId={userId}
                    eventId={eventId}
                    pluginId={pluginId}
                    onFilesDrop={onFilesDrop}
                    className="w-full overflow-hidden rounded-2xl bg-white bg-opacity-20"
                  >
                    <div className="flex flex-col items-center justify-center gap-2 p-4">
                      <PluginImage imageKey={"imgCameraAdd"} alt="mySvgImage" />
                      <p className="text-white/70 text-base text-end mt-1">
                        Upload photo
                      </p>
                    </div>
                  </FileDropzone>
                </div>
              )}
            </div>
          </div>
        </Modal>
        {/* Single Image Modal */}
        <Modal
          isOpen={showImageModal}
          onRequestClose={onCloseImageModal}
          className="fixed inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center outline-none"
          overlayClassName="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
          ariaHideApp={false}
        >
          <div
            className="text-white/30 text-base cursor-pointer p-2 bg-white/30 rounded-full absolute top-8 right-6"
            onClick={onCloseImageModal}
          >
            <IoClose color={"white"} size={20} />
          </div>
          {selectedImage && (
            <div className="flex flex-col items-center w-full max-w-md mx-auto p-4">
              <img
                src={selectedImage.url}
                alt="Selected event photo"
                className="rounded-lg w-full max-h-[60vh] object-contain bg-black"
              />
              <div className="mt-3 flex w-full justify-between items-center text-white">
                <span className="text-sm text-white/70">
                  {selectedImage.timestamp
                    ? formatFullTimestamp(selectedImage.timestamp)
                    : ""}
                </span>
                {isHost && (
                  <button
                    onClick={() => onDeleteImage(selectedImage)}
                    className="flex items-center justify-center gap-1.5 text-sm text-red-500 hover:text-red-400 py-0 bg-transparent pr-0"
                  >
                    <IoTrashOutline className="h-4 w-4" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          )}
        </Modal>
      </>
    )
  },
  InfoModalComponent,
  InfoStepsCard,

  // Utility: Get cached plugin event instance data (localStorage-based)
  getCachedPluginEventInstanceData: async (eventId, pluginId, onUpdate) => {
    const cacheKey = `plugin_event_instance_cache_${eventId}_${pluginId}`
    const cached = localStorage.getItem(cacheKey)
    let parsed = {}
    if (cached) {
      try {
        parsed = JSON.parse(cached)
      } catch {
        // ignore parse error, fallback to API
      }
    }
    // Start API call in background
    getPluginEventInstanceData(eventId, pluginId).then((result) => {
      if (result.success) {
        localStorage.setItem(cacheKey, JSON.stringify(result.data))
        if (onUpdate) onUpdate(result.data)
      }
    })

    return parsed
  },
  // Utility: Set cached plugin event instance data (localStorage-based)
  setCachedPluginEventInstanceData: (eventId, pluginId, data) => {
    const cacheKey = `plugin_event_instance_cache_${eventId}_${pluginId}`
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
      try {
        const parsed = JSON.parse(cached)
        localStorage.setItem(cacheKey, JSON.stringify({ ...parsed, ...data }))
      } catch {
        // ignore parse error, fallback to API
        localStorage.setItem(cacheKey, JSON.stringify(data))
      }
    } else {
      localStorage.setItem(cacheKey, JSON.stringify(data))
    }
  },
}
